/** Parent details tab. */
import * as React from 'react';
import { User, Calendar, BarChart3, TrendingUp } from 'lucide-react';
import { LabelBadge } from '@/components/common/LabelBadge';
import { DetailsCard } from '@/components/common/DetailsCard';
import { DetailField } from '@/components/common/details/DetailField';
import {
  LOGGED_IN_CONFIG,
  type ParentStatusConfig,
  type REGISTRATION_SOURCE_CONFIG,
} from '@/components/parents/parent-configs';
import {
  completionBarColor,
  daysSince,
  formatDate,
  formatRegistrationManagerName,
} from '@/components/parents/parent-table-utils';
import { useQuery } from '@tanstack/react-query';
import type { ParentRecord } from '@/types/parent.types';
import { getUserHistory } from '@/services/history.service';
import { InternalCommentBox } from '@/components/common/InternalCommentBox';
import { getRoleLabel } from '@/utils/role-label-utils';

/** Props for {@link ParentDetailsTab}. */
interface ParentDetailsTabProps {
  record: ParentRecord;
  detailRecord: ParentRecord;
  statusConfig: ParentStatusConfig | undefined;
  sourceConfig: (typeof REGISTRATION_SOURCE_CONFIG)[string] | null;
}

/** Parent details tab. */
export const ParentDetailsTab: React.FC<ParentDetailsTabProps> = ({
  record,
  detailRecord,
  statusConfig,
  sourceConfig,
}) => {
  const isArchived = record.user?.status === 'archived';
  const { data: history } = useQuery({
    queryKey: ['history', 'user', record.userId],
    queryFn: () => getUserHistory(record.userId),
    enabled: isArchived && !!record.userId,
  });
  const archive = history?.archive;
  const archiveReason =
    archive?.comment && archive.reason
      ? `${archive.reason} — ${archive.comment}`
      : (archive?.reason ?? archive?.comment ?? '—');

  return (
    <div className="details-view-grid mt-6">
      <DetailsCard icon={Calendar} title="Dates" colorName="color-2" colSpan={1}>
        <div className="space-y-3">
          <DetailField label="Date d'inscription" value={formatDate(record.createdAt)} />
          <DetailField label="Ancienneté" value={daysSince(record.createdAt)} />
          <DetailField label="Dernière mise à jour" value={formatDate(record.updatedAt)} />
          {isArchived && archive ? (
            <DetailField label="Date d'archivage" value={formatDate(archive.archivedAt)} />
          ) : null}
        </div>
      </DetailsCard>

      <DetailsCard icon={BarChart3} title="Métriques" colorName="color-1" colSpan={1}>
        <div className="space-y-3">
          <DetailField
            label="Statut"
            value={
              statusConfig ? (
                <LabelBadge
                  label={statusConfig.label}
                  icon={statusConfig.icon}
                  variant={statusConfig.badgeVariant}
                  color={statusConfig.badgeColor}
                  size="sm"
                />
              ) : (
                record.onboardingStatus
              )
            }
          />
          <DetailField
            label={getRoleLabel('coordinator')}
            value={
              formatRegistrationManagerName(detailRecord) === '—' ? (
                '—'
              ) : (
                <LabelBadge
                  label={formatRegistrationManagerName(detailRecord)}
                  icon={User}
                  variant="outlined"
                  color="rgb(var(--color-1-nuance-1))"
                  size="sm"
                />
              )
            }
          />
          <DetailField
            label="Source"
            value={
              sourceConfig ? (
                <LabelBadge
                  label={sourceConfig.label}
                  icon={sourceConfig.icon}
                  variant={sourceConfig.badgeVariant}
                  color={sourceConfig.badgeColor}
                  size="sm"
                />
              ) : (
                '—'
              )
            }
          />
          <DetailField
            label="Connecté"
            value={
              <LabelBadge
                label={LOGGED_IN_CONFIG[record.hasLoggedIn ? 'connected' : 'never'].label}
                icon={LOGGED_IN_CONFIG[record.hasLoggedIn ? 'connected' : 'never'].icon}
                variant={LOGGED_IN_CONFIG[record.hasLoggedIn ? 'connected' : 'never'].badgeVariant}
                color={LOGGED_IN_CONFIG[record.hasLoggedIn ? 'connected' : 'never'].badgeColor}
                size="sm"
              />
            }
          />
          <DetailField label="Enfants" value={`${(record as any)._childrenCount ?? 0} enfant(s)`} />
          {isArchived && archive ? (
            <>
              <DetailField label="Archivé par" value={archive.archivedBy?.userFullName ?? '—'} />
              <DetailField label="Motif" value={archiveReason} />
            </>
          ) : null}
        </div>
      </DetailsCard>

      <DetailsCard icon={TrendingUp} title="Progression" colorName="color-3" colSpan={2}>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">
              {record.completion.completedSections}/{record.completion.totalSections} sections
              complétées
            </span>
            <span className="text-sm text-muted-foreground">
              {record.completion.completedSectionsPercentage}%
            </span>
          </div>
          <div className="w-full h-3 bg-[var(--color-1-nuance-3)] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${record.completion.completedSectionsPercentage}%`,
                backgroundColor: completionBarColor(record.completion.completedSectionsPercentage),
              }}
            />
          </div>
        </div>
      </DetailsCard>
      <InternalCommentBox userId={record.userId} comment={record.user?.internalComment ?? null} />
    </div>
  );
};
